import {
  BarLayer,
  BucketLineLayer,
  ChartFrame,
  Legend,
  sumBuckets,
  valueDomain,
  stackExtent,
  ZeroLine,
} from '@finsim/charts';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { categorySeries } from '../lib/series';
import { ChartCard } from './chart-card';
import type { CanvasData } from './use-canvas';

const NET = 'var(--text-1)';

export function CashFlow({ data }: { data: CanvasData }) {
  const { t } = useTranslation();
  const { project, focused, focusedResult, valueMode, buckets, fmt, range, selectedItemId } = data;

  const categories = useMemo(
    () =>
      focusedResult
        ? categorySeries(project, focusedResult, valueMode, {
            uncategorized: t('charts.uncategorized'),
            other: t('charts.other'),
          })
        : [],
    [project, focusedResult, valueMode, t],
  );
  const yearly = useMemo(
    () => categories.map((c) => ({ ...c, values: sumBuckets(c.values, buckets) })),
    [categories, buckets],
  );
  const net = useMemo(() => {
    const out = new Float64Array(buckets.length);
    for (const c of yearly) c.values.forEach((v, i) => (out[i] = (out[i] ?? 0) + v));
    return out;
  }, [yearly, buckets.length]);
  const income = useMemo(
    () =>
      focusedResult
        ? sumBuckets(focusedResult.series.income[valueMode], buckets)
        : new Float64Array(),
    [focusedResult, valueMode, buckets],
  );
  const savingsRate = useMemo(
    () => Float64Array.from(net, (v, i) => ((income[i] ?? 0) > 0 ? v / (income[i] ?? 1) : 0)),
    [net, income],
  );

  // Item focus: highlight the selected item's category.
  const selected = selectedItemId ? project.items[selectedItemId] : undefined;
  const focusCategory = selected && selected.type !== 'asset' ? (selected.category ?? null) : null;

  const top = useMemo(() => {
    if (!focusedResult || !focused) return [];
    const scenario = project.scenarios.find((s) => s.id === focused.id);
    return (scenario?.itemIds ?? [])
      .flatMap((id) => {
        const item = project.items[id];
        const series = focusedResult.items[id];
        if (!item || !series || item.type === 'asset') return [];
        let total = 0;
        for (let i = range[0]; i <= range[1]; i++) total += series.cash[valueMode][i] ?? 0;
        return total === 0 ? [] : [{ id, name: item.name, total }];
      })
      .sort((a, b) => Math.abs(b.total) - Math.abs(a.total))
      .slice(0, 5);
  }, [project, focused, focusedResult, range, valueMode]);

  if (!focused || !focusedResult) return null;
  const [lo, hi] = stackExtent(
    yearly.map((c) => c.values),
    buckets.length,
  );
  const subtitle = [focused.name, data.moneyCaption].filter(Boolean).join(' · ');
  const bucketLabel = (b: (typeof buckets)[number]): string =>
    b.partial ? t('charts.partialYear', { year: b.year }) : String(b.year);
  const bucketAt = (i: number): number => buckets.findIndex((b) => i >= b.from && i <= b.to);
  const grossTotal = top.reduce((a, x) => a + Math.abs(x.total), 0);

  return (
    <div className="space-y-4 p-4">
      <ChartCard
        title={t('charts.cashFlowTitle')}
        subtitle={subtitle}
        legend={
          <Legend
            label={t('charts.cashFlowTitle')}
            items={[
              ...categories.map((c) => ({ id: c.id, label: c.label, color: c.color })),
              { id: 'net', label: t('charts.netFlow'), color: NET },
            ]}
          />
        }
        table={{
          caption: t('charts.cashFlowTitle'),
          columns: [t('charts.year'), ...yearly.map((c) => c.label), t('charts.netFlow')],
          rows: buckets.map((b, i) => ({
            label: bucketLabel(b),
            values: [...yearly.map((c) => fmt.money(c.values[i] ?? 0)), fmt.money(net[i] ?? 0)],
          })),
        }}
      >
        <ChartFrame
          markers={data.markers}
          dates={data.dates}
          range={range}
          height={300}
          yDomain={valueDomain([lo, hi, net])}
          formatY={fmt.compact}
          formatXTick={fmt.year}
          ariaLabel={t('charts.cashFlowAria', { name: focused.name })}
          renderTooltip={(i) => {
            const b = bucketAt(i);
            const bucket = buckets[b];
            if (!bucket) return null;
            return (
              <div className="space-y-1">
                <p className="text-text-3">{bucketLabel(bucket)}</p>
                {yearly.map((c) =>
                  (c.values[b] ?? 0) === 0 ? null : (
                    <p key={c.id} className="flex items-center justify-between gap-4">
                      <span className="flex items-center gap-1.5 text-text-2">
                        <span
                          aria-hidden
                          className="size-2 rounded-sm"
                          style={{ background: c.color }}
                        />
                        {c.label}
                      </span>
                      <span className="tabular">{fmt.money(c.values[b] ?? 0)}</span>
                    </p>
                  ),
                )}
                <p className="flex justify-between gap-4 border-t border-border-subtle pt-1 font-medium">
                  <span>{t('charts.netFlow')}</span>
                  <span className="tabular">{fmt.money(net[b] ?? 0)}</span>
                </p>
              </div>
            );
          }}
        >
          <ZeroLine />
          <BarLayer
            buckets={buckets}
            series={yearly.map((c) => ({
              id: c.id,
              values: c.values,
              color: c.color,
              muted: focusCategory !== null && c.id !== focusCategory,
            }))}
          />
          <BucketLineLayer buckets={buckets} values={net} color={NET} />
        </ChartFrame>
      </ChartCard>

      <div className="grid gap-4 xl:grid-cols-2">
        <ChartCard
          title={t('charts.savingsRate')}
          subtitle={focused.name}
          table={{
            caption: t('charts.savingsRate'),
            columns: [t('charts.year'), t('charts.savingsRate')],
            rows: buckets.map((b, i) => ({
              label: bucketLabel(b),
              values: [fmt.percent(savingsRate[i] ?? 0)],
            })),
          }}
        >
          <ChartFrame
            markers={data.markers}
            dates={data.dates}
            range={range}
            height={200}
            yDomain={valueDomain([savingsRate])}
            formatY={fmt.percent}
            formatXTick={fmt.year}
            ariaLabel={t('charts.savingsRateAria', { name: focused.name })}
            renderTooltip={(i) => {
              const b = bucketAt(i);
              const bucket = buckets[b];
              return bucket ? (
                <p className="flex justify-between gap-4">
                  <span className="text-text-3">{bucketLabel(bucket)}</span>
                  <span className="tabular font-medium">{fmt.percent(savingsRate[b] ?? 0)}</span>
                </p>
              ) : null;
            }}
          >
            <ZeroLine />
            <BucketLineLayer buckets={buckets} values={savingsRate} color="var(--chart-series-1)" />
          </ChartFrame>
        </ChartCard>

        <section className="rounded-lg border border-border-subtle bg-bg-1 px-4 pt-3 pb-3">
          <h2 className="mb-2 text-sm font-medium">{t('charts.topItems')}</h2>
          <table className="w-full text-sm">
            <caption className="sr-only">{t('charts.topItems')}</caption>
            <thead>
              <tr className="text-xs text-text-3">
                <th scope="col" className="pb-1.5 text-left font-normal">
                  {t('charts.item')}
                </th>
                <th scope="col" className="pb-1.5 text-right font-normal">
                  {t('charts.total')}
                </th>
                <th scope="col" className="w-24 pb-1.5 text-right font-normal">
                  {t('charts.share')}
                </th>
              </tr>
            </thead>
            <tbody>
              {top.map((row) => (
                <tr key={row.id} className="border-t border-border-subtle">
                  <th scope="row" className="py-1.5 text-left font-normal text-text-2">
                    {row.name}
                  </th>
                  <td
                    className={`tabular py-1.5 text-right ${row.total < 0 ? 'text-negative' : ''}`}
                  >
                    {fmt.money(row.total)}
                  </td>
                  <td className="py-1.5 pl-3">
                    <div className="flex items-center justify-end gap-2">
                      <span className="tabular text-xs text-text-3">
                        {fmt.percent(grossTotal ? Math.abs(row.total) / grossTotal : 0)}
                      </span>
                      <span aria-hidden className="h-1.5 w-12 overflow-hidden rounded-full bg-bg-3">
                        <span
                          className="block h-full rounded-full bg-text-3"
                          style={{
                            width: `${String(grossTotal ? (Math.abs(row.total) / grossTotal) * 100 : 0)}%`,
                          }}
                        />
                      </span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      </div>
    </div>
  );
}
